import React, { useState, useRef, useEffect } from 'react';

const Services = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [velocity, setVelocity] = useState(0);
  const scrollContainerRef = useRef(null);
  const lastXRef = useRef(0);
  const lastTimeRef = useRef(0);
  const animationRef = useRef(null);

  const services = [
    {
      title: 'Sistemas Web e Plataformas',
      description: 'Soluções sob medida para automatizar, escalar e descomplicar a operação.',
      image: '/images/services/sistemas.jpg',
    },
    {
      title: 'Landing Pages e Sites',
      description: 'Para quem quer presença digital que converte – sem enrolação.',
      image: '/images/services/landing.jpg',
    },
    {
      title: 'Aplicativos',
      description: 'Do MVP ao app publicado, com performance, UX e estética no ponto.',
      image: '/images/services/apps.jpg',
    },
    {
      title: 'Automação com IA',
      description: 'Inteligência artificial aplicada para otimizar processos e decisões.',
      image: '/images/services/ia.jpg',
    },
    {
      title: 'UX/UI Design',
      description: 'Interface com propósito, usabilidade com leveza e identidade visual.',
      image: '/images/services/design.jpg',
    },
  ];

  const applyMomentum = () => {
    if (Math.abs(velocity) < 0.5) {
      setVelocity(0);
      return;
    }

    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollLeft -= velocity;
    setVelocity(velocity * 0.95);

    animationRef.current = requestAnimationFrame(applyMomentum);
  };

  const handleMouseDown = (e) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    setIsDragging(true);
    setVelocity(0);
    const container = scrollContainerRef.current;
    setStartX(e.pageX - container.offsetLeft);
    setScrollLeft(container.scrollLeft);
    lastXRef.current = e.pageX;
    lastTimeRef.current = Date.now();
    
    container.style.scrollBehavior = 'auto';
    container.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const container = scrollContainerRef.current;
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startX) * 1.5;
    
    container.scrollLeft = scrollLeft - walk;
    
    const now = Date.now();
    const timeDelta = now - lastTimeRef.current;
    const distanceDelta = e.pageX - lastXRef.current;
    
    if (timeDelta > 0) {
      setVelocity(distanceDelta / timeDelta * 10);
    }
    
    lastXRef.current = e.pageX;
    lastTimeRef.current = now;
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    const container = scrollContainerRef.current;
    container.style.cursor = 'grab';
    
    if (Math.abs(velocity) > 1) {
      animationRef.current = requestAnimationFrame(applyMomentum);
    }
  };

  const handleTouchStart = (e) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    setIsDragging(true);
    setVelocity(0);
    const container = scrollContainerRef.current;
    setStartX(e.touches[0].pageX - container.offsetLeft);
    setScrollLeft(container.scrollLeft);
    lastXRef.current = e.touches[0].pageX;
    lastTimeRef.current = Date.now();
    
    container.style.scrollBehavior = 'auto';
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    const container = scrollContainerRef.current;
    const x = e.touches[0].pageX - container.offsetLeft;
    const walk = (x - startX) * 1.5;
    
    container.scrollLeft = scrollLeft - walk;
    
    const now = Date.now();
    const timeDelta = now - lastTimeRef.current;
    const distanceDelta = e.touches[0].pageX - lastXRef.current;
    
    if (timeDelta > 0) {
      setVelocity(distanceDelta / timeDelta * 10);
    }
    
    lastXRef.current = e.touches[0].pageX;
    lastTimeRef.current = now;
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    if (Math.abs(velocity) > 1) {
      animationRef.current = requestAnimationFrame(applyMomentum);
    }
  };

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <section className="py-12 md:py-20 bg-gray-950 overflow-hidden">
      {/* Título centralizado */}
      <div className="text-center mb-10 md:mb-16 px-4 select-none">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
          <span className="text-white">Nossos </span>
          <span className="text-emerald-500">Serviços</span>
        </h2>
        <p className="text-gray-400 text-base md:text-lg">
          Soluções completas em desenvolvimento de software e design digital
        </p>
      </div>

      {/* Container do carrossel */}
      <div className="relative">
        <div
          ref={scrollContainerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="flex gap-4 md:gap-6 overflow-x-auto scrollbar-hide cursor-grab select-none pl-[5%] sm:pl-[8%] md:pl-[10%] pr-[5%] sm:pr-[8%] md:pr-[10%]"
          style={{
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {services.map((service, index) => (
            <div
              key={index}
              className="flex-shrink-0 w-[280px] sm:w-[320px] md:w-[360px]"
            >
              {/* Card com imagem - Tamanho reduzido */}
              <div className="relative h-[280px] sm:h-[320px] md:h-[360px] rounded-2xl md:rounded-3xl overflow-hidden mb-4 md:mb-6 group">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  draggable="false"
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>

              {/* Texto abaixo - Responsivo */}
              <div className="select-none px-1">
                <h3 className="text-base md:text-lg font-bold text-white mb-1 md:mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-400 text-xs leading-relaxed">
                  {service.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;